import { AUTH_ACTION_TYPE } from 'enums';
import { useCallback, useContext, useMemo } from 'react';
import { t } from 'services/i18n.service';
import { AuthPageContext } from '../AuthActionsWrapper';
import {
  AUTH_SCREEN,
  EMPTY_STRING,
  LOCAL_STORAGE
} from 'constants/general';
import { PARAMS } from 'constants/params';
import AppleLogo from 'assets/icons/AppleLogo';
import {
  getAuth0User,
  isDesktopLoginRequest,
  loginWithApple
} from 'services/auth.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { onboardFlowSignInSelector } from 'store/reducer/onboard-flow/selectors';
import { toast } from 'react-toastify';
import { updateIsNewUserSigninProcess } from 'store/reducer/setting/slice';
import { ROUTES } from 'constants/routes';
import BaseButton from 'components/ui/BaseButton';
import { removeLocalStorage } from 'utils/helpers/storage';
import {
  captureException,
  capturePostHogEvent
} from 'services/posthog.service';
import { useNavigate } from 'react-router-dom';
import { POST_HOG_EVENT } from 'constants/events';
import { isMobileDevice } from 'utils/device.util';
import { isSupportPanelPage } from 'utils/support-panel.util';

const AppleAuth = () => {
  const {
    state: {
      isProcessing,
      appleLoginInProgress,
      googleLoginInProgress,
      screen
    },
    dispatch
  } = useContext(AuthPageContext);
  const appDispatch = useAppDispatch();
  const isNewUserSigninProcess = useAppSelectorShallow(
    onboardFlowSignInSelector
  );
  const navigate = useNavigate();

  const handleLoginCallback = useCallback(
    async (hasError: boolean, error?: string) => {
      if (hasError) {
        error && toast.error(error);
        dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING });
        dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_APPLE_LOGIN_IN_PROGRESS });
        captureException(error);
        capturePostHogEvent(POST_HOG_EVENT.APPLE_LOGIN.ERROR, {
          message: error
        });
      } else if (isSupportPanelPage()) {
        // Agents sign in to the Zoho-framed panel, not up: skip onboarding and stay on its path.
        capturePostHogEvent(POST_HOG_EVENT.SIGN_IN.APPLE_SIGN_IN_SUCCESS);
        navigate(ROUTES.SUPPORT_PANEL, { replace: true });
      } else {
        let nextRoute = ROUTES.HOME;
        const auth0AppleUser = await getAuth0User();
        const isFirstLoggedIn =
          auth0AppleUser?.['https://dashboard.focusbear.io/first_login'] ??
          false;
        let eventName = POST_HOG_EVENT.SIGN_IN.APPLE_SIGN_IN_SUCCESS;
        if (isFirstLoggedIn) {
          appDispatch(updateIsNewUserSigninProcess(true));
          eventName = isDesktopLoginRequest
            ? POST_HOG_EVENT.SIGN_UP.REDIRECT_TO_DESKTOP_SUCCESS
            : POST_HOG_EVENT.SIGN_UP.APPLE_SIGN_UP_SUCCESS;
          const nextNonMobileRoute = isDesktopLoginRequest
            ? `${ROUTES.DESKTOP_REDIRECT}?${PARAMS.SIGN_INTO_DESKTOP}=true`
            : ROUTES.DOWNLOAD_APPS;

          const isMobile = isMobileDevice();
          nextRoute = isMobile
            ? ROUTES.MOBILE_APP_DOWNLOAD
            : nextNonMobileRoute;
        } else {
          eventName = isDesktopLoginRequest
            ? POST_HOG_EVENT.SIGN_IN.REDIRECT_TO_DESKTOP_SUCCESS
            : eventName;
          nextRoute = isDesktopLoginRequest
            ? `${ROUTES.DESKTOP_REDIRECT}?${PARAMS.SIGN_INTO_DESKTOP}=true`
            : nextRoute;
          isDesktopLoginRequest &&
            removeLocalStorage([LOCAL_STORAGE.AUTH_BEARER_TOKEN]);
        }
        capturePostHogEvent(eventName);
        navigate(nextRoute, { replace: true });
      }
    },
    [appDispatch, dispatch, navigate]
  );

  const handleContinueWithApple = useCallback(() => {
    capturePostHogEvent(POST_HOG_EVENT.APPLE_LOGIN.CLICKED, {
      screen: screen === AUTH_SCREEN.SIGN_IN ? 'sign-in' : 'sign-up'
    });
    dispatch?.({
      type: AUTH_ACTION_TYPE.UPDATE_APPLE_LOGIN_IN_PROGRESS,
      value: true
    });
    loginWithApple((hasError, error) => {
      handleLoginCallback(hasError, error);
    });
  }, [screen, dispatch, handleLoginCallback]);

  const isAnyAuthActionInProgress = useMemo(
    () =>
      isProcessing ||
      appleLoginInProgress ||
      googleLoginInProgress ||
      isNewUserSigninProcess,
    [
      isProcessing,
      appleLoginInProgress,
      googleLoginInProgress,
      isNewUserSigninProcess
    ]
  );

  return (
    <BaseButton
      title={
        appleLoginInProgress
          ? EMPTY_STRING
          : t(
              `auth_page.${screen === AUTH_SCREEN.SIGN_IN ? 'continue_with_apple' : 'sign_up_with_apple'}`
            )
      }
      icon={
        appleLoginInProgress ? undefined : (
          <AppleLogo styles='~w-4/6 h-auto mr-1' />
        )
      }
      additionalStyles='~min-w-44/96 w-fit ~px-5/10 py-1/2 self-center hover-scale'
      variant='secondary'
      disabled={isAnyAuthActionInProgress}
      onClick={handleContinueWithApple}
      isLoading={appleLoginInProgress}
    />
  );
};

export default AppleAuth;
